import PropTypes from "prop-types";
import { useState, useEffect } from "react";
import * as sentry from "@sentry/browser";
import { Modal, PrimaryButton, TextButton, InputTextarea } from "@artprov/design-system";
import MessageHeader from "../../../common/components/messageHeader/MessageHeader.jsx";
import { useFacturaStore } from "../../../common/zustand/factura.store.js";
import { useComprasAuth } from "../../../common/hooks/useComprasAuth.js";
import { getActionConfig, getDescriptionContext } from "./modalActionConfig.js";

export default function ModalApproval({ isOpen, onClose, idComprobante, amount = null, onApprove, onError, mode = "aprobado" }) {
  const facturaActual = useFacturaStore((state) => state.facturaActual);
  const setFacturaActual = useFacturaStore((state) => state.setFacturaActual);
  const detalleFactura = useFacturaStore((state) => state.detalleFacturaProveedor);
  const { username, userDetails } = useComprasAuth();
  const [comment, setComment] = useState("");
  const [textareaHints, setTextareaHints] = useState(undefined);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Obtener configuración dinámica según el mode
  const config = getActionConfig(mode);
  
  const safeClose = () => {
    if (typeof onClose === "function") onClose();
  };

  const handleCommentChange = (event) => {
    const rawValue = typeof event === 'string' ? event : (event?.target?.value ?? '');
    const value = String(rawValue).slice(0, 250);
    setComment(value);
    
    // Validación dinámica según configuración
    if (config.commentRequired && config.minCommentLength > 0) {
      const len = value.length;
      const hasText = len > 0;
      const isValid = len >= config.minCommentLength;
      setTextareaHints(
        hasText 
          ? (isValid ? [] : [{ variant: 'error', text: `El comentario debe tener al menos ${config.minCommentLength} caracteres.` }])
          : undefined
      );
    } else {
      setTextareaHints(undefined);
    }
  };

  const handleSubmitClick = async () => {
    try {
      // Validación antes de enviar
      if (config.commentRequired && (!comment || comment.trim().length < config.minCommentLength)) {
        return;
      }
      
      setIsSubmitting(true);
      const centroCosto = userDetails?.CENTRO_COSTO || null;
      
      // Llamar al servicio configurado dinámicamente
      const response = await config.serviceFn(
        idComprobante,
        comment || null,
        username,
        centroCosto
      );
      
      // Actualizar store con el nuevo estado
      if (typeof setFacturaActual === 'function') {
        const next = { ...(facturaActual || {}), ESTADOCOMPROBANTE: config.newStatus };
        setFacturaActual(next);
      }
      
      if (typeof onApprove === 'function') {
        onApprove({ idComprobante, comment, response, mode });
      }
    } catch (error) {
      sentry.captureException(error);
      if (typeof onError === 'function') {
        onError(error);
      }
    } finally {
      setIsSubmitting(false);
      safeClose();
    }
  };

  // Datos derivados
  const derivedAmount = amount ?? facturaActual?.IMPORTE;
  const typeInvoice = facturaActual?.TIPO;
  const razonSocial = detalleFactura?.RAZON_SOCIAL;

  // Contexto para descripción dinámica
  const descriptionContext = getDescriptionContext({
    amount: derivedAmount,
    typeInvoice,
    razonSocial,
    userDetails
  });

  // Obtener descripción (puede ser función o string)
  const description = typeof config.description === 'function' 
    ? config.description(descriptionContext)
    : config.description;

  useEffect(() => {
    const len = String(comment || '').trim().length;
    const isCommentValid = !config.commentRequired || len >= config.minCommentLength;
    setSubmitDisabled(!isCommentValid);
  }, [comment, config.commentRequired, config.minCommentLength]);
  
  return (
    <>
      {isOpen && (
        <div className="relative z-[999]">
          <Modal.Container
            open={isOpen}
            setOpen={safeClose}
            closeOnClickOutside={false}
            onClose={safeClose}
          >
            <Modal.Content 
              height={config.height} 
              width="520px" 
              className="overflow-visible" 
              variant={config.variant}
            >
              <Modal.Header className="flex pl-4">
                <MessageHeader
                  variant={config.messageHeaderVariant}
                  title={config.title}
                  description={description}
                  titleClassName={config.titleClassName || ""}
                />
                <Modal.Close onClose={safeClose} />
              </Modal.Header>
              <Modal.Body>
                <div className="modal-content w-[456px]">
                  <InputTextarea
                    label="Comentario"
                    placeholder={config.commentRequired ? "Agregar comentario" : "Agregar comentario (opcional)"}
                    value={comment}
                    onChange={handleCommentChange}
                    maxLength={250}
                    hints={textareaHints}
                    isRequired={config.commentRequired}
                  />
                </div>
              </Modal.Body>
              <Modal.Footer className="flex justify-center">
                <div className="flex space-x-2">
                  <TextButton size="small" onClick={safeClose} disabled={isSubmitting}>
                    Cancelar
                  </TextButton>
                  <PrimaryButton 
                    size="small" 
                    onClick={handleSubmitClick} 
                    classNameIcon="text-tertiary-100" 
                    disabled={submitDisabled || isSubmitting}
                  >
                    {config.buttonText}
                  </PrimaryButton>
                </div>
              </Modal.Footer>
            </Modal.Content>
          </Modal.Container>
        </div>
      )}
    </>
  );
}

ModalApproval.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  idComprobante: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onApprove: PropTypes.func,
  onError: PropTypes.func,
  mode: PropTypes.string,
};
